import { parsePhoneNumber } from "react-phone-number-input";

/** Return a compact international number for phone verification requests. */
export const normalizePhoneNumberForOtp = (value?: string | null): string => {
  const phoneNumber = value?.trim() ?? "";
  if (!phoneNumber) return "";

  try {
    const parsedPhoneNumber = parsePhoneNumber(phoneNumber);
    if (parsedPhoneNumber) return parsedPhoneNumber.number;
  } catch {
    // Fall back to removing whitespace so malformed values still receive a
    // clean, user-friendly validation response from the API.
  }

  return phoneNumber.replace(/\s+/g, "");
};

export const isPhoneNumberFormatError = (error: unknown): boolean => {
  const root =
    error && typeof error === "object"
      ? (error as Record<string, unknown>)
      : {};
  const data =
    root.data && typeof root.data === "object"
      ? (root.data as Record<string, unknown>)
      : {};
  const detailItems = Array.isArray(data.detail)
    ? data.detail
    : data.detail && typeof data.detail === "object"
      ? [data.detail]
      : [];
  const details = detailItems.length
    ? detailItems
        .map((item) => {
          if (!item || typeof item !== "object") return "";
          const detail = item as Record<string, unknown>;
          return [detail.type, detail.msg, detail.message]
            .filter(Boolean)
            .join(" ");
        })
        .join(" ")
    : typeof data.detail === "string"
      ? data.detail
      : "";
  const message = [data.message, details, root.message]
    .filter((part): part is string => typeof part === "string")
    .join(" ");

  return /regex|regular expression|pattern|string_pattern_mismatch|does not match|invalid.*(phone|number)|(phone|number).*(invalid|format|country code)/i.test(
    message,
  );
};

export const INVALID_PHONE_NUMBER_MESSAGE =
  "Enter a valid phone number with its country code, then try again.";

const getApiErrorMessage = (error: unknown): string => {
  const root =
    error && typeof error === "object"
      ? (error as Record<string, unknown>)
      : {};
  const data =
    root.data && typeof root.data === "object"
      ? (root.data as Record<string, unknown>)
      : {};
  const detailItems = Array.isArray(data.detail)
    ? data.detail
    : data.detail && typeof data.detail === "object"
      ? [data.detail]
      : [];
  const detailMessage = detailItems
    .map((item) => {
      if (!item || typeof item !== "object") return "";
      const detail = item as Record<string, unknown>;
      return [detail.type, detail.msg, detail.message]
        .filter((value): value is string => typeof value === "string")
        .join(" ");
    })
    .join(" ");

  return [data.message, detailMessage, data.detail, root.message]
    .filter((value): value is string => typeof value === "string")
    .join(" ");
};

export const isPhoneOtpCooldownError = (error: unknown): boolean =>
  /cooldown|wait before|rate.?limit|too many requests/i.test(
    getApiErrorMessage(error),
  );

export const getFriendlyPhoneOtpSendError = (error: unknown): string => {
  if (isPhoneNumberFormatError(error)) return INVALID_PHONE_NUMBER_MESSAGE;
  if (isPhoneOtpCooldownError(error)) {
    return "Please wait before requesting another code.";
  }
  return "We couldn’t send a verification code. Please try again.";
};

export const getFriendlyPhoneOtpVerificationError = (
  error: unknown,
): string => {
  const message = getApiErrorMessage(error);
  if (/attempts exceeded|too many incorrect/i.test(message)) {
    return "Too many incorrect attempts. Request a new code.";
  }
  if (/expired|no longer active|challenge.*missing/i.test(message)) {
    return "This code has expired. Request a new code.";
  }
  if (/does not match the current phone|phone.*mismatch/i.test(message)) {
    return "This code was sent to a different phone number. Request a new code.";
  }
  return "We couldn’t verify that code. Check it and try again.";
};
